import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    BuildStatus,
    ListResult,
    ModuleCategoryV1Read,
    ModuleComplianceV1Read,
    ModuleLabelKey,
    ModuleShareRead,
    ModuleSharesPayload,
    ModuleSpecification,
    ModuleStep,
    ModuleSubmissionProducerRead,
    ModuleSubmissionV1,
    ModuleUpdate,
    ModuleV1Read,
    OptionItem,
    ProgrammingLanguage,
    RepositoryState,
    ShareUserRole,
    SingleResponse,
    SubmissionState,
    UpdateAndSubmitData,
    UserInfo,
    VersionReadModel,
    WorkflowV1Read,
} from '@ca/core/models';
import {
    BreadcrumbAndTitleService,
    DialogService,
    LinkService,
    ModuleCategoriesV1Service,
    ModuleCompliancesV1Service,
    ModuleSharingService,
    ModulesV1Service,
    ModulesWorkspaceV1Service,
    UserNotificationService,
    UserService,
    WindowRef,
} from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { getModuleLabelKey, isInAdmin, isInWorkspace, parseBool, updateDefaultApeerMetaTags } from '@ca/utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, forkJoin, from, of, zip } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { Keyboard, default as Swiper, default as SwiperCore, SwiperOptions } from 'swiper';
import { MyModulesPublishError, MyModulesUpdateError } from '../../../app-store/my-modules-store/my-modules.reducer';
import { PUBLIC_MODULES_ROUTE, WIKI_URL } from '../../constants/constants';
import { FullNamePipe } from '../../pipes/full-name/full-name.pipe';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import { ModuleEditorComponent } from '../module-editor/module-editor.component';
import { ModuleInviteComponent } from '../module-invite/module-invite.component';
import { ModulePublishComponent } from '../module-publish/module-publish.component';
import { ModuleSpecGeneratorComponent } from '../module-spec-generator/module-spec-generator.component';
import { WorkflowCreatorComponent } from '../workflow-creator/workflow-creator.component';

export interface EditorModalFunctions {
    updateModule: Function;
    updateLatestPublicVersion: Function;
}

export interface PublishModalFunctions {
    updateAndSubmitModule: Function;
}

@Component({
    selector: 'ca-module-detail',
    templateUrl: './module-detail.component.html',
    styleUrls: ['./module-detail.component.scss'],
})
export class ModuleDetailComponent implements OnInit, OnDestroy {
    @Input() isReadOnly = false;
    @Input() moduleId: string;
    @Input() isNewModule: boolean;
    @Input() shouldLatestPublicVersionBeShown = false;
    @Input() set selectedModule(module: ModuleV1Read) {
        if (module) {
            this._selectedModule = module;
            if (this.editorModalRef?.componentInstance?.isSubmitting) {
                this.userNotificationService.hideSpinner();
                this.editorModalRef.componentInstance.isSubmitting = false;
                this.editorModalRef.close(module);
            } else if (this.publishModalRef?.componentInstance?.isSubmitting) {
                this.userNotificationService.hideSpinner();
                this.publishModalRef.componentInstance.isSubmitting = false;
                this.publishModalRef.close(module);
            }
        }
    }
    get selectedModules(): ModuleV1Read {
        return this._selectedModule;
    }

    @Input() set modulesError(error: MyModulesUpdateError | MyModulesPublishError) {
        if (error) {
            if (this.editorModalRef?.componentInstance?.isSubmitting) {
                this.userNotificationService.hideSpinner();
                this.editorModalRef.componentInstance.isSubmitting = false;
            } else if (this.publishModalRef?.componentInstance?.isSubmitting) {
                this.userNotificationService.hideSpinner();
                this.publishModalRef.componentInstance.isSubmitting = false;
            }
        }
    }

    // When the module is in Review, we have to get its information from another endpoint
    @Input() isModuleInReview = false;

    // for loading animation of module detail modal
    @Output() onHasFinishedLoading = new EventEmitter<void>();
    @Output() onDeletedModule = new EventEmitter<ModuleV1Read>();
    @Output() onUpdatedModule = new EventEmitter<ModuleUpdate>();
    @Output() onUpdatedLatestPublicVersion = new EventEmitter<ModuleUpdate>();
    @Output() onUpdatedAndSubmittedModule = new EventEmitter<UpdateAndSubmitData>();
    @ViewChild('flowSection', { static: true }) flowSection: ElementRef;
    module: ModuleV1Read;

    canEditModuleAsContentAdmin = false;
    isCodeExpanded = true;
    isHistoryExpanded = false;

    author?: UserInfo;
    wikiUrl = WIKI_URL;
    repositoryUrl: string = '';
    categories: ModuleCategoryV1Read[] = [];
    shownVersion?: VersionReadModel;
    compliances: OptionItem<string>[];
    showCredentials: boolean = false;
    buildStatus = BuildStatus.Unknown;
    moduleSteps: ModuleStep[] = [
        { title: 'MODULES.DETAIL.STEPS.ONE.TITLE', description: 'MODULES.DETAIL.STEPS.ONE.DESC' },
        { title: 'MODULES.DETAIL.STEPS.TWO.TITLE', description: 'MODULES.DETAIL.STEPS.TWO.DESC' },
        { title: 'MODULES.DETAIL.STEPS.THREE.TITLE', description: 'MODULES.DETAIL.STEPS.THREE.DESC' },
        { title: 'MODULES.DETAIL.STEPS.FOUR.TITLE', description: 'MODULES.DETAIL.STEPS.FOUR.DESC' },
        { title: 'MODULES.DETAIL.STEPS.FIVE.TITLE', description: 'MODULES.DETAIL.STEPS.FIVE.DESC' },
        { title: 'MODULES.DETAIL.STEPS.SIX.TITLE', description: 'MODULES.DETAIL.STEPS.SIX.DESC' },
    ];
    programmingLanguages: ProgrammingLanguage[] = [
        {
            title: 'Python',
            icon: 'assets/images/illustrations/python.svg',
            url: 'https://docs.apeer.com/tutorials/python-ome-tiff-example',
        },
        {
            title: 'ImageJ',
            icon: 'assets/images/illustrations/imagej.svg',
            url: 'https://docs.apeer.com/tutorials/imagej-example',
        },
        { title: 'Java', icon: 'assets/images/illustrations/java.svg', url: 'https://docs.apeer.com/tutorials/java-example' },
        { title: 'Matlab', icon: 'assets/images/illustrations/matlab.svg', url: 'https://docs.apeer.com/tutorials/matlab-linux-example' },
    ];

    config: SwiperOptions = {
        a11y: {
            enabled: true,
        },
        direction: 'horizontal',
        slidesPerView: 3,
        slideToClickedSlide: true,
        mousewheel: false,
        scrollbar: false,
        watchSlidesProgress: true,
        navigation: true,
        keyboard: {
            enabled: true,
        },
        pagination: false,
        centeredSlides: true,
        loop: true,
        roundLengths: true,
        slidesOffsetBefore: 100,
        slidesOffsetAfter: 100,
        spaceBetween: 50,
    };
    stepIndex = 0;
    moduleDisabledMessage: string = '';
    createWorkflowDisabledMessage: string = '';
    private editorModalRef: NgbModalRef;
    private publishModalRef: NgbModalRef;
    private _selectedModule: ModuleV1Read;

    get isBuildStatusSucceeded(): boolean {
        return this.buildStatus === BuildStatus.Succeeded;
    }

    get isLoggedIn(): boolean {
        return this.userService.isLoggedIn;
    }

    get userInfo(): UserInfo | undefined {
        return this.userService.myInfo;
    }

    get isUnshareable(): boolean {
        let isUnshareable = false;
        if (this.module?.shares?.length > 0) {
            const userSub = this.userInfo?.sub;
            if (!this.isMine && userSub != null) {
                isUnshareable = this.module.shares.some((su: ModuleShareRead) => su.userId === userSub);
            }
        }

        return isUnshareable;
    }

    get latestPublicDetailLink(): string[] {
        let latestPublicDetailLink: string[] = [];
        if (this.module?.title != null) {
            latestPublicDetailLink = this.linkService.getDetailLinkAsArray(PUBLIC_MODULES_ROUTE, this.module.title, this.module.id);
        }
        return latestPublicDetailLink;
    }

    get isInWorkspace(): boolean {
        return isInWorkspace(this.router.url);
    }

    /**
     * We have two ways to determine if it's a module in review:
     *      1. Either the URL contains /admin/ (when coming from submitted-modules)
     *      2. isModuleInReview is true (when opening the ModuleDetails page in a dialog)
     */
    get isSubmittedModuleInReview(): boolean {
        return isInAdmin(this.router.url) || this.isModuleInReview;
    }

    get isMine(): boolean {
        let isMine = false;
        if (this.userInfo && this.module?.producerId) {
            isMine = this.userInfo && this.userInfo.sub === this.module.producerId;
        }
        return isMine;
    }

    get codeDownloadUrl(): string | undefined {
        return this.module?.latestPublicVersion?.codeDownloadUrl;
    }

    get isOpenSourceModule(): boolean {
        let isOpenSource: boolean = false;

        if (this.module?.latestPublicVersion && !this.isInWorkspace) {
            isOpenSource = this.module.latestPublicVersion.isOpenSource;
        }
        return isOpenSource;
    }

    get isPublicModule(): boolean {
        return this.module?.latestPublicVersion != null;
    }

    /**
     * Indicates whether the module is shared, and not public.
     * So the module is either shared WITH me, or shared BY me.
     */
    get isSharedModule(): boolean {
        return this.module?.shares?.length > 0 && this.module.latestPublicVersion == null;
    }

    /**
     * Indicates that this module is shared WITH me (meaning: I'm not the producer of this module)
     * and I have write access.
     */
    get isSharedWithWriteAccess(): boolean {
        let sharedWithWriteAccess: boolean = false;
        if (this.userInfo && this.module?.shares?.length > 0) {
            const myShareRecord: ModuleShareRead | undefined = this.module.shares.find((su: ModuleShareRead) => su.userId === this.userInfo!.sub);
            if (myShareRecord != null) {
                sharedWithWriteAccess = myShareRecord.role === ShareUserRole.Contributor;
            }
        }

        return sharedWithWriteAccess;
    }

    get isInReview(): boolean {
        return !!this.module?.submissions?.some((s: ModuleSubmissionProducerRead) => s.state === SubmissionState.inReview);
    }

    get isPublishable(): boolean {
        return this.isMine && !this.isInReview;
    }

    get isEditable(): boolean {
        return ((this.isMine || this.isSharedWithWriteAccess) && !this.isInReview) || this.canEditModuleAsContentAdmin;
    }

    get isDeletable(): boolean {
        return this.isMine && !this.module.latestPublicVersion;
    }

    get moduleLabelKey(): ModuleLabelKey | undefined {
        return this.module ? getModuleLabelKey(this.module, this.userInfo?.sub) : undefined;
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        private breadcrumbAndTitleService: BreadcrumbAndTitleService,
        private dialogService: DialogService,
        private fullNamePipe: FullNamePipe,
        private linkService: LinkService,
        private meta: Meta,
        private modalService: NgbModal,
        private moduleCategoriesV1Service: ModuleCategoriesV1Service,
        private moduleComplianceV1Service: ModuleCompliancesV1Service,
        private moduleSharingService: ModuleSharingService,
        private modulesV1Service: ModulesV1Service,
        private modulesWorkspaceV1Service: ModulesWorkspaceV1Service,
        private router: Router,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private userService: UserService,
        private windowRef: WindowRef,
        private cdRef: ChangeDetectorRef,
    ) {
        this.moduleDisabledMessage = this.translateService.instant('MODULES.PUBLISH_MODULE_DISABLED');
        this.createWorkflowDisabledMessage = this.translateService.instant('MODULES.CREATE_WORKFLOW_DISABLED');
        SwiperCore.use([Keyboard]);
    }

    ngOnInit() {
        this.init();
    }

    ngOnDestroy() {
        updateDefaultApeerMetaTags(this.meta, this.translateService);
    }

    disableCreateWorkflowButton(): boolean {
        let isDisabled = false;
        if (!this.isPublicModule && this.buildStatus !== BuildStatus.Unknown && this.buildStatus !== BuildStatus.Succeeded) {
            isDisabled = true;
        }

        return isDisabled;
    }

    scrollToFlow(): void {
        if (this.isNewModule && this.flowSection != null) {
            setTimeout(() => {
                this.flowSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }
    }
    onIndexChange(swiper: Swiper[]): void {
        this.stepIndex = swiper[0].realIndex;
        this.cdRef.detectChanges();
    }
    editModule(): void {
        this.editorModalRef = this.modalService.open(ModuleEditorComponent);
        const editorComponent = <ModuleEditorComponent>this.editorModalRef.componentInstance;
        editorComponent.module = this.module;
        editorComponent.isEditingAsAdmin = this.canEditModuleAsContentAdmin;
        editorComponent.categories = this.categories;
        editorComponent.compliances = this.compliances;
        editorComponent.functions = {
            updateModule: (moduleUpdateModel: ModuleUpdate): void => {
                this.onUpdatedModule.emit(moduleUpdateModel);
            },
            updateLatestPublicVersion: (moduleUpdateModel: ModuleUpdate): void => {
                this.onUpdatedLatestPublicVersion.emit(moduleUpdateModel);
            },
        } as EditorModalFunctions;

        this.editorModalRef.result
            .then((module: ModuleV1Read) => {
                this.setupDetail(module);
            })
            .catch(() => {
                // do nothing
            });
    }

    /**
     * Creates a workflow with this module.
     * First, checks if the Module has already been successfully built - this is checked by the buildId (not being null)
     * The build ID can be in draftVersion (if it's my module) or in latestPublicVersion (if it's a public module)
     */
    createWorkflowWithModule(module: ModuleV1Read): void {
        const errMessage = this.translateService.instant('MODULES.MY_MODULES.MODULE_NOT_BUILT_YET');
        if (
            (module.draftVersion && module.draftVersion.specification == null) ||
            (module.latestPublicVersion && module.latestPublicVersion.specification == null)
        ) {
            this.userNotificationService.showError(errMessage);
            throw new Error(errMessage);
        }

        const modalRef = this.modalService.open(WorkflowCreatorComponent);
        modalRef.componentInstance.isPublicModule = this.isPublicModule;
        modalRef.componentInstance.module = module;
        modalRef.result
            .then((result: SingleResponse<WorkflowV1Read>) => {
                if (result.data != null) {
                    this.router.navigate(['/workflows/workflow-editor', result.data.id]);
                }
            })
            .catch(() => {
                // do nothing
            });
    }

    publishModule(): void {
        this.publishModalRef = this.modalService.open(ModulePublishComponent);
        const publishComponent = <ModulePublishComponent>this.publishModalRef.componentInstance;
        publishComponent.module = this.module;
        publishComponent.categories = this.categories;
        publishComponent.compliances = this.compliances;
        this.publishModalRef.componentInstance.functions = {
            updateAndSubmitModule: (moduleUpdateData: ModuleUpdate, submissionData: ModuleSubmissionV1): void => {
                this.onUpdatedAndSubmittedModule.emit({ moduleUpdateData, submissionData });
            },
        } as PublishModalFunctions;
        this.publishModalRef.result
            .then((module: ModuleV1Read) => {
                this.setupDetail(module);
            })
            .catch(() => {
                // do nothing
            });
    }

    editSpecModule(): void {
        const modalRef = this.modalService.open(ModuleSpecGeneratorComponent, { size: 'lg', windowClass: 'specModal', backdrop: 'static' });
        const specificationComponent = <ModuleSpecGeneratorComponent>modalRef.componentInstance;

        specificationComponent.specification = this.module.selectedModuleVersion.specification;
        specificationComponent.moduleTitle = this.module.title;

        modalRef.result
            .then((module: ModuleV1Read) => {
                this.setupDetail(module);
            })
            .catch(() => {
                // do nothing
            });
    }

    invite(): void {
        const modalRef = this.modalService.open(ModuleInviteComponent, { size: 'lg' });
        const inviteComponent = <ModuleInviteComponent>modalRef.componentInstance;
        inviteComponent.module = this.module;

        modalRef.result
            .then((moduleShares: ModuleShareRead[]) => {
                this.module.shares = moduleShares;
            })
            .catch(() => {
                // do nothing
            });
    }

    openContactUs(): void {
        this.modalService.open(ContactUsComponent, { size: 'lg' });
    }

    /**
     * Unshare and Delete Module are the same HTTP requests (backend handles the distinction then).
     */
    deleteOrUnshareModule(): void {
        let confirmModal: NgbModalRef;
        if (this.isDeletable) {
            confirmModal = this.dialogService.openConfirm({
                message: 'Do you really want to delete this module?',
                confirmText: 'Delete',
                cancelText: 'Cancel',
                showCancel: true,
                showClose: true,
                options: { size: 'md' },
            });
        } else if (this.isUnshareable) {
            confirmModal = this.dialogService.openConfirm({
                message: 'Do you really want to remove this shared module from your workspace?',
                confirmText: 'Unshare',
                cancelText: 'Cancel',
                showCancel: true,
                showClose: true,
                options: { size: 'md' },
            });
        }

        confirmModal!.result
            .then(async () => {
                if (this.module.id != null) {
                    this.onDeletedModule.emit(this.module);
                } else {
                    console.error('Could not delete module since no ID was provided. Please check!');
                }
            })
            .catch(() => {
                // do nothing
            });
    }

    updateRepositoryUrl(repository: RepositoryState) {
        let host = this.windowRef.nativeWindow.location.origin;
        if (this.windowRef.isLocalhost) {
            host = 'https://' + environment.name + '.dis-apeer.com';
        }
        this.buildStatus = repository.buildStatus;
        this.repositoryUrl = repository ? `${host}/_git/${repository.repositoryName}` : '';
    }

    private setupDetail(module: ModuleV1Read): void {
        this.module = module;

        if (module.selectedModuleVersion != null) {
            this.shownVersion = module.selectedModuleVersion;
        } else {
            this.shownVersion = this.isInWorkspace ? module.draftVersion : module.latestPublicVersion;
        }

        if (this.shownVersion && this.shownVersion.image) {
            this.shownVersion.image = this.shownVersion.image.replace(' ', '%20');
        }

        // 317242: A new module (with Empty example) has no specification.
        // Here we create it to avoid errors in the spec generator
        if (module.selectedModuleVersion.specification == null) {
            module.selectedModuleVersion.specification = new ModuleSpecification();
        }

        if (!this.isInWorkspace && this.isLoggedIn && this.userInfo) {
            this.canEditModuleAsContentAdmin = this.userInfo.isAdmin;
        }
    }

    private setModuleDetailBreadcrumb(): void {
        if (this.isSubmittedModuleInReview) {
            this.breadcrumbAndTitleService.setBreadcrumbToSubmittedModules().appendBreadcrumbItem({
                titleKey: 'MODULES.DETAIL.BROWSER_TITLE_MODULE_TITLE',
                titleParams: { title: this.module.title },
                route: this.router.url,
            });
        } else if (this.isInWorkspace) {
            this.breadcrumbAndTitleService.setBreadcrumbToUserModules().appendBreadcrumbItem({
                titleKey: 'MODULES.DETAIL.BROWSER_TITLE_MODULE_TITLE',
                titleParams: { title: this.module.title },
                route: this.router.url,
            });
        } else {
            this.breadcrumbAndTitleService.setBreadcrumbToPublicModules().appendBreadcrumbItem({
                titleKey: 'MODULES.DETAIL.BROWSER_TITLE_MODULE_TITLE',
                titleParams: { title: this.module.title },
                route: this.router.url,
            });
        }
    }

    private init(): void {
        if (this.moduleId == null) {
            this.activatedRoute.params
                .pipe(
                    tap((params: Params) => {
                        this.moduleId = params['moduleId'];
                    }),
                )
                .subscribe();
            this.activatedRoute.queryParams
                .pipe(
                    tap((params: Params) => {
                        this.isNewModule = params.isNew != null ? parseBool(params.isNew) : false;
                    }),
                )
                .subscribe();
        }
        if (this.moduleId == null) {
            throw new Error('No module ID provided. It must be provided either by a route param or by passing it as Input parameter (in the dialog)');
        }

        let module$: Observable<ModuleV1Read>;
        if (this.isSubmittedModuleInReview) {
            module$ = this.modulesV1Service.getSubmittedModuleById(this.moduleId);
        } else if (!this.isInWorkspace) {
            module$ = this.isLoggedIn ? this.modulesV1Service.getModuleById(this.moduleId) : this.modulesV1Service.getPublicModuleById(this.moduleId);
        } else {
            module$ = from(this.modulesWorkspaceV1Service.getById(this.moduleId));
        }

        const moduleAndAuthor$ = module$.pipe(
            switchMap((module: ModuleV1Read) => {
                const ids = [module].map((entity: ModuleV1Read) => entity.producerId);
                let moduleShare$: Observable<ModuleSharesPayload> = of({ moduleShares: [] });

                // If the module is shared WITH me or BY me, we need to get the shares with names
                // The names (first name and last name of the share) are only fetched with this separate request, since this
                // is a performance consuming operation on backend.
                if (this.isInWorkspace && this.userInfo && this.userInfo.sub === module.producerId && module.shares.length > 0) {
                    moduleShare$ = this.moduleSharingService.getModuleSharesWithUserdata(module.id);
                }

                return zip(this.userService.getUserInfos$(ids), of(module), moduleShare$);
            }),
            map((zipped: [UserInfo[], ModuleV1Read, ModuleSharesPayload]) => {
                const [userInfos, , moduleShares] = zipped;
                let [, module] = zipped;

                if (moduleShares != null && moduleShares.moduleShares.length > 0) {
                    module.shares = moduleShares.moduleShares;
                }
                this.author = userInfos.find((user: UserInfo) => user.id === module.producerId);

                if (this.shouldLatestPublicVersionBeShown) {
                    module = new ModuleV1Read(module, module.latestPublicVersion);
                }
                return module;
            }),
            first(),
        );

        const categories$ = from(this.moduleCategoriesV1Service.getList()).pipe(first());

        forkJoin([moduleAndAuthor$, categories$])
            .pipe(
                tap((results: [ModuleV1Read, ListResult<ModuleCategoryV1Read>]) => {
                    const [module, categoriesList] = results;

                    this.setupCategories(categoriesList.data);
                    this.setupDetail(module);
                    this.setModuleMetaTags(module);

                    this.setModuleDetailBreadcrumb();
                    this.scrollToFlow();
                    this.onHasFinishedLoading.emit();
                }),
                catchError((error: unknown) => {
                    console.error('Fetching Module or Categories failed: ', error);
                    return EMPTY;
                }),
            )
            .subscribe();

        this.fetchCompliances$()
            .pipe(
                tap((c: OptionItem<string>[]) => {
                    this.compliances = c;
                }),
                catchError((err: HttpErrorResponse) => {
                    console.error('Failed fetching module compliances', err);
                    const errorMessage = err.message ? err.message : JSON.stringify(err);
                    this.userNotificationService.showError(errorMessage || 'Failed fetching compliances');
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private setModuleMetaTags(module: ModuleV1Read): void {
        if (module.title != null) {
            this.meta.addTag({ name: 'title', content: module.title });
            this.meta.updateTag({ property: 'og:title', content: module.title });
        }
        if (this.shownVersion != null) {
            this.meta.addTag({ name: 'description', content: this.shownVersion.summary });
            this.meta.updateTag({ property: 'og:description', content: this.shownVersion.summary });
        }
        if (this.author != null) {
            this.meta.addTag({ name: 'author', content: this.fullNamePipe.transform(this.author) });
        }
        if (module.latestPublicVersion && module.latestPublicVersion.image) {
            this.meta.updateTag({ property: 'og:image', content: module.latestPublicVersion.image });
        }
        this.meta.updateTag({ property: 'og:url', content: this.windowRef.nativeWindow?.location.href });

        const category = module.categoryName ? `, ${module.categoryName}` : '';
        const keywords = `apeer.com, apeer, module, Source Code, ${module.title}${category}`;
        this.meta.addTag({ name: 'keywords', content: keywords });
    }

    private setupCategories(data: ModuleCategoryV1Read[]) {
        if (data !== null) {
            this.categories = data;
        }
    }

    private fetchCompliances$(): Observable<OptionItem<string>[]> {
        return this.moduleComplianceV1Service.getCompliances().pipe(
            map((compliances: ModuleComplianceV1Read[]) => {
                return compliances.map(c => new OptionItem<string>(c.id, c.name));
            }),
        );
    }
}
